import React from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { phoneSearch } from '@/reports/SearchPhoneForm/phoneSearch';
import { emailSearch, usernameSearch } from '@/reports/index';
import {
  Accordion,
  AccordionProps,
  Box,
  styled,
  Text,
  Icons,
  Divider,
} from '@/theme/index';
import {
  schemaEmailSearchForm,
  schemaPhoneSearchForm,
  schemaUsernameSearchForm,
} from '@/utils/schemaForms/schemaForms';
import { useScreenSize } from '@/utils/useScreenSize';
import { DynamicContactSearchForm } from './DynamicContactSearchForm/DynamicContactSearchForm';

const FilterAccordion = styled((props: AccordionProps) => (
  <Accordion defaultExpanded disableGutters square {...props} />
))(({ theme }) => ({
  margin: 0,
  border: 0,
  color: theme.palette.text.primary,

  '&:hover': {
    boxShadow: 'none',
  },

  '& .MuiAccordionSummary-root, & .MuiAccordionDetails-root': {
    paddingLeft: 0,
    paddingRight: 0,
  },

  '& .MuiAccordionSummary-content .MuiTypography-root': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },
  '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': {
    color: `${theme.palette.primary.main} !important`,
  },
}));

const FiltersContainer = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(3)}`,
  backgroundColor: theme.palette.common.white,
  borderRadius: '8px',
  marginBottom: theme.spacing(2.5),
}));

export interface SideBarReportSearchProps {
  isFormCollapsed?: boolean;
}

interface ResponseError extends Error {
  response: {
    status: number;
  };
}

export const SideBarReportSearch: React.FC<SideBarReportSearchProps> = ({
  isFormCollapsed,
}) => {
  const { routingUtils } = useAppConfigContext();
  const { isMedium } = useScreenSize();
  const { navigate } = routingUtils;

  const handleError = (error: Error) => {
    if ((error as ResponseError).response.status === 426) {
      navigate('/upgrade/plan');
    }
  };

  return (
    <>
      {(!isMedium || (isMedium && !isFormCollapsed)) && (
        <FiltersContainer>
          <Text variant="h3" lineHeight={2}>
            <Icons.Tune sx={{ m: 0.75, mb: -0.75 }} color="primary" />
            Search by Contact Info
          </Text>
          <Divider sx={{ mt: 3, mb: 2 }} />
          <Box>
            <FilterAccordion display="Email">
              <DynamicContactSearchForm
                searchType="email"
                mutationFn={emailSearch}
                schema={schemaEmailSearchForm}
                placeholder="Email Address"
                inputName="email"
                dataCy="email_header_field"
                onError={handleError}
              />
            </FilterAccordion>
          </Box>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Box>
            <FilterAccordion display="Phone">
              <DynamicContactSearchForm
                searchType="phone"
                mutationFn={phoneSearch}
                schema={schemaPhoneSearchForm}
                placeholder="(123) 456-7890"
                inputName="phone"
                dataCy="phone_header_field"
                type="tel"
                onError={handleError}
              />
            </FilterAccordion>
          </Box>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Box>
            <FilterAccordion display="Username Search">
              <DynamicContactSearchForm
                searchType="username"
                mutationFn={usernameSearch}
                schema={schemaUsernameSearchForm}
                placeholder="Username"
                inputName="username"
                dataCy="username_header_field"
                onError={handleError}
              />
            </FilterAccordion>
          </Box>
        </FiltersContainer>
      )}
    </>
  );
};
